// Breakpoint selection and layout resolution for the live dashboard page.
export const dashboardLiveBreakpointComputed = {
    /**
     * Get the runtime breakpoint that currently matches the viewport.
     */
    activeRuntimeBreakpointId() {
        return this.getOriginalRuntimeBreakpointId();
    },
};

const dashboardLiveBreakpoints = {
    /**
     * Normalize runtime breakpoints for live dashboard rendering.
     *
     * @param {*} breakpoints
     */
    normalizeRuntimeBreakpoints(breakpoints) {
        const sourceBreakpoints = Array.isArray(breakpoints) ? breakpoints : [];
        const originalBreakpoint = sourceBreakpoints.find((breakpoint) => breakpoint?.isOriginal)
            || sourceBreakpoints[0]
            || { id: 'desktop' };

        return [{ ...originalBreakpoint, id: originalBreakpoint.id || 'desktop', isOriginal: true }];
    },
    /**
     * Get original runtime breakpoint id.
     */
    getOriginalRuntimeBreakpointId() {
        for (const breakpoint of this.breakpoints) {
            if (breakpoint.isOriginal) {
                return breakpoint.id;
            }
        }

        return this.breakpoints[0]?.id || null;
    },
    /**
     * Build media query feature.
     *
     * @param {*} feature
     */
    buildMediaQueryFeature(feature) {
        if (!feature?.name || typeof feature.value === 'undefined') {
            return '';
        }

        return `(${feature.name}: ${feature.value})`;
    },
    /**
     * Build media query branch.
     *
     * @param {*} branch
     */
    buildMediaQueryBranch(branch) {
        const mediaTypes = Array.isArray(branch?.mediaTypes)
            ? branch.mediaTypes.filter(Boolean)
            : [];
        const featureQueries = Array.isArray(branch?.features)
            ? branch.features.map((feature) => this.buildMediaQueryFeature(feature)).filter(Boolean)
            : [];
        const queryParts = [];

        if (mediaTypes.length) {
            queryParts.push(mediaTypes.join(', '));
        }

        if (featureQueries.length) {
            queryParts.push(featureQueries.join(' and '));
        }

        return queryParts.join(' and ');
    },
    /**
     * Build runtime breakpoint query.
     *
     * @param {*} breakpoint
     */
    buildRuntimeBreakpointQuery(breakpoint) {
        const mediaQuerySubList = Array.isArray(breakpoint?.mediaQuery?.mediaQuerySubList)
            ? breakpoint.mediaQuery.mediaQuerySubList
            : [];
        const branches = mediaQuerySubList
            .map((branch) => this.buildMediaQueryBranch(branch))
            .filter(Boolean);

        return branches.join(', ');
    },
    /**
     * Breakpoint matches viewport.
     *
     * @param {*} breakpoint
     */
    breakpointMatchesViewport(breakpoint) {
        const mediaQuery = this.buildRuntimeBreakpointQuery(breakpoint);

        if (!mediaQuery || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return false;
        }

        return window.matchMedia(mediaQuery).matches;
    },
    /**
     * Normalize grid number.
     *
     * @param {*} value
     * @param {*} fallback
     * @param {*} max
     */
    normalizeGridNumber(value, fallback, max = null) {
        const parsed = Number.parseInt(value, 10);

        if (!Number.isFinite(parsed)) {
            return fallback;
        }

        const normalized = Math.max(parsed, 1);
        return max ? Math.min(normalized, max) : normalized;
    },
    /**
     * Get dashboard grid row count.
     *
     * @param {*} widgets
     */
    getDashboardGridRowCount(widgets) {
        let maxRow = 4;

        for (const widget of widgets) {
            const layout = this.getWidgetLayout(widget);

            if (layout.hidden) {
                continue;
            }

            const rowEnd = layout.row + layout.rowSpan - 1;

            if (rowEnd > maxRow) {
                maxRow = rowEnd;
            }
        }

        return Math.max(maxRow, 4);
    },
    /**
     * Get widget layout.
     *
     * @param {*} widget
     */
    getWidgetLayout(widget) {
        const attributes = this.getWidgetAttributes(widget);
        const layout = typeof attributes.layout === 'object' ? attributes.layout : {};

        /*
         * Normalize layout values before rendering so incomplete dashboard data
         * cannot place widgets outside the visible grid.
         */
        const column = this.normalizeGridNumber(layout.column, 1, this.dashboardGridColumns);
        const row = this.normalizeGridNumber(layout.row, 1);
        const maxColumnSpan = Math.max((this.dashboardGridColumns + 1) - column, 1);

        return {
            column,
            row,
            columnSpan: this.normalizeGridNumber(layout.columnSpan, this.dashboardGridColumns, maxColumnSpan),
            rowSpan: this.normalizeGridNumber(layout.rowSpan, 1),
            hidden: Boolean(layout.hidden),
        };
    },
};

export default dashboardLiveBreakpoints;
